// ==============================================
// Device Health Tab
// ==============================================
//
// Primary operator surface for inspecting a single device's kiosk hardening
// state. Renders the latest kiosk_state snapshot, a comparison against the
// previous snapshot, and a short recent-history timeline.
//
// Data comes from the existing admin update-log endpoint
// `GET /api/admin/neptune/updates?device_id=X&limit=N` — rows ordered desc by
// checkedAt. The launcher's `device_meta.kiosk_state` block (vortexnav-client
// PR #365 / Launcher 2.42.x+) is the source of truth for these snapshots.
//
// No backend change is required. The health-rule logic lives in
// `utils/kioskHealth.ts` and is reused; this file is render-only.

import { Fragment, useCallback, useEffect, useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import {
  ArrowPathIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { copyToClipboard } from '../../lib/clipboard';
import {
  compareKiosk,
  computeKioskHealth,
  computeKioskHealthV2,
  detectKioskSchema,
  healthLabelText,
  healthLabelTone,
  kioskFieldDisplays,
  type KioskCompareResult,
  type KioskFieldDisplay,
  type KioskHealthTone,
  type KioskHealthV2Result,
  type KioskSchema,
  type KioskStateAny,
  type KioskStateV2,
  type KioskV2SectionResult,
} from '../../utils/kioskHealth';
import {
  buildKioskHealthReport,
  type KioskHealthReportInput,
  type KioskHealthReportRow,
} from '../../utils/kioskHealthReport';
import HealthModuleCard, {
  type HealthModuleBadge,
  type HealthModuleTone,
} from './HealthModuleCard';
import KioskV2SectionCard from './KioskV2SectionCard';
import RuntimeDriftCard from './RuntimeDriftCard';

/**
 * Optional device metadata shape consumed by the Copy-report action. Kept
 * deliberately narrow — only the operator-visible identity fields. Other
 * Device fields (registration_code, last_ip_address, device_fingerprint,
 * etc.) are intentionally excluded so they cannot reach the clipboard.
 */
export interface DeviceHealthTabDeviceInfo {
  name?: string | null;
  serial_number?: string | null;
  la_version?: string | null;
  last_seen_at?: string | null;
}

interface DeviceHealthTabProps {
  deviceId: string;
  device?: DeviceHealthTabDeviceInfo | null;
}

// History window — match other tabs' refetch cadence (60s) and a sane row
// budget for the timeline. Each row is small (~200-400 bytes); 20 rows is
// ~4-8 KB JSON over the wire.
const HISTORY_LIMIT = 20;
const HISTORY_DISPLAY = 10;

const TONE_PILL: Record<KioskHealthTone, { pill: string; dot: string }> = {
  ok: { pill: 'border-green-700/50 bg-green-900/30 text-green-300', dot: 'bg-green-400' },
  warn: { pill: 'border-amber-700/50 bg-amber-900/30 text-amber-300', dot: 'bg-amber-400' },
  fail: { pill: 'border-red-700/50 bg-red-900/30 text-red-300', dot: 'bg-red-400' },
  neutral: { pill: 'border-slate-700 bg-slate-800/50 text-slate-300', dot: 'bg-slate-400' },
};

const FIELD_VALUE_TONE: Record<KioskFieldDisplay['tone'], string> = {
  ok: 'text-green-400',
  warn: 'text-amber-400',
  fail: 'text-red-400',
  unreadable: 'text-slate-500 italic',
  info: 'text-slate-300',
};

// Group the 10 kiosk_state field display cards into the three operator-facing
// categories used in the device health tab. Order matches kioskFieldDisplays().
const FIELD_GROUPS: { title: string; range: [number, number] }[] = [
  { title: 'Privilege', range: [0, 3] }, // UID, Platform-signed, Device Owner
  { title: 'Kiosk mode', range: [3, 6] }, // Lock task, Navigation mode, Snapshot complete
  { title: 'Gesture hardening', range: [6, 10] }, // back gesture L/R, one-handed, accessibility magnification
];

// Local shape for one update-log row from /api/admin/neptune/updates. The
// admin endpoint returns deviceMeta as `unknown`; we narrow here. All fields
// optional — older rows may lack any of them. `kiosk_state` can be v1 (flat)
// or v2 (sectioned); detect with `detectKioskSchema`.
interface UpdateLogRow {
  id?: string | number;
  checkedAt?: string;
  result?: string;
  deviceMeta?: {
    launcher_version?: string;
    android_version?: string;
    device_model?: string;
    kiosk_state?: KioskStateAny | null;
    [key: string]: unknown;
  };
  update_channel?: string;
  [key: string]: unknown;
}

function extractKiosk(row: UpdateLogRow | undefined): KioskStateAny | null {
  if (!row || !row.deviceMeta || typeof row.deviceMeta !== 'object') return null;
  const ks = row.deviceMeta.kiosk_state;
  if (ks && typeof ks === 'object') return ks as KioskStateAny;
  return null;
}

function extractLauncherVersion(row: UpdateLogRow | undefined): string | undefined {
  if (!row || !row.deviceMeta || typeof row.deviceMeta !== 'object') return undefined;
  const v = row.deviceMeta.launcher_version;
  return typeof v === 'string' ? v : undefined;
}

function extractUpdateChannel(row: UpdateLogRow | undefined): string | undefined {
  if (!row) return undefined;
  return typeof row.update_channel === 'string' ? row.update_channel : undefined;
}

function formatTimestamp(iso?: string): string {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'd MMM yyyy HH:mm:ss');
  } catch {
    return iso;
  }
}

function formatRelative(iso?: string): string {
  if (!iso) return '—';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return iso;
  }
}

// ─── Sub-components ──────────────────────────────────────────────────────

function HealthBadge({
  tone,
  text,
  size = 'lg',
}: {
  tone: KioskHealthTone;
  text: string;
  size?: 'lg' | 'sm';
}) {
  const cls = TONE_PILL[tone];
  const sizing =
    size === 'lg'
      ? 'px-3 py-1 text-sm gap-2'
      : 'px-2 py-0.5 text-[10px] gap-1.5';
  const dotSize = size === 'lg' ? 'h-2 w-2' : 'h-1 w-1';
  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium ${cls.pill} ${sizing}`}
    >
      <span className={`rounded-full ${cls.dot} ${dotSize}`} />
      {text}
    </span>
  );
}

function FieldRow({ field }: { field: KioskFieldDisplay }) {
  return (
    <div className="grid grid-cols-[1fr,auto,auto] items-center gap-3 px-3 py-1.5 text-sm">
      <span className="text-slate-300">{field.label}</span>
      <span className={`font-mono text-xs ${FIELD_VALUE_TONE[field.tone]}`}>{field.value}</span>
      {field.tone !== 'ok' ? (
        <span className="text-[10px] text-slate-500 font-mono">expected {field.expected}</span>
      ) : (
        <span className="text-[10px] text-green-400/70 font-mono">ok</span>
      )}
    </div>
  );
}

function CurrentSnapshotCard({ state }: { state: KioskStateAny }) {
  const fields = kioskFieldDisplays(state);
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/50">
      <div className="border-b border-slate-700 px-4 py-2.5">
        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Current snapshot
        </h4>
      </div>
      <div className="divide-y divide-slate-700/50">
        {FIELD_GROUPS.map((group) => {
          const groupFields = fields.slice(group.range[0], group.range[1]);
          return (
            <div key={group.title} className="px-1 py-2">
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {group.title}
              </div>
              <div className="divide-y divide-slate-800/50">
                {groupFields.map((f) => (
                  <FieldRow key={f.label} field={f} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CompareWithPrevious({
  latest,
  previous,
  previousAt,
}: {
  latest: KioskStateAny;
  previous: KioskStateAny | null;
  previousAt?: string;
}) {
  const [expanded, setExpanded] = useState(false);

  if (previous == null) {
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-800/50 px-4 py-3 text-sm text-slate-400">
        No previous snapshot to compare with for this device yet.
      </div>
    );
  }

  // Field-by-field diff (uses the displays for stable labels and order).
  const latestFields = kioskFieldDisplays(latest);
  const previousFields = kioskFieldDisplays(previous);
  const diffs = latestFields
    .map((nowField, i) => ({ now: nowField, then: previousFields[i] }))
    .filter((p) => p.now.value !== p.then.value);

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/50">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-700/30"
      >
        <span className="flex items-center gap-2">
          {expanded ? (
            <ChevronDownIcon className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-slate-400" />
          )}
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Compare with previous snapshot
          </span>
          <span className="text-[11px] text-slate-500">({formatRelative(previousAt)})</span>
        </span>
        <span className="text-[11px] text-slate-500">
          {diffs.length === 0
            ? 'no fields changed'
            : `${diffs.length} field${diffs.length === 1 ? '' : 's'} changed`}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-slate-700 px-1 py-2">
          {diffs.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400">
              No kiosk fields changed since previous snapshot.
            </div>
          ) : (
            <div className="grid grid-cols-[1fr,auto,auto,auto] items-center gap-3 px-2 text-xs">
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Field
              </div>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Now
              </div>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Previous
              </div>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                —
              </div>
              {diffs.map((d) => (
                <FragmentRow key={d.now.label} now={d.now} then={d.then} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FragmentRow({ now, then }: { now: KioskFieldDisplay; then: KioskFieldDisplay }) {
  return (
    <>
      <div className="px-2 py-1 text-sm text-slate-300">{now.label}</div>
      <div className={`px-2 py-1 font-mono text-xs ${FIELD_VALUE_TONE[now.tone]}`}>{now.value}</div>
      <div className={`px-2 py-1 font-mono text-xs ${FIELD_VALUE_TONE[then.tone]}`}>
        {then.value}
      </div>
      <div className="px-2 py-1 text-[10px] text-amber-400/80">changed</div>
    </>
  );
}

function HistoryTimeline({ rows }: { rows: UpdateLogRow[] }) {
  if (rows.length === 0) return null;
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/50">
      <div className="border-b border-slate-700 px-4 py-2.5">
        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
          Recent update checks
        </h4>
      </div>
      <div className="divide-y divide-slate-700/50">
        {rows.slice(0, HISTORY_DISPLAY).map((row) => {
          const ks = extractKiosk(row);
          const schema = detectKioskSchema(ks);
          // Schema-aware badge: dispatcher already routes v1 vs v2 rules.
          const health = computeKioskHealth(ks);
          const tone = healthLabelTone(health.primary);
          const text = healthLabelText(health.primary);
          const launcherVersion =
            typeof row.deviceMeta === 'object' && row.deviceMeta && 'launcher_version' in row.deviceMeta
              ? (row.deviceMeta.launcher_version as string | undefined)
              : undefined;
          const schemaTag =
            schema === 'v2' ? 'v2' : schema === 'v1' ? 'v1' : schema === 'unknown' ? '?' : null;
          return (
            <div
              key={String(row.id ?? row.checkedAt ?? Math.random())}
              className="grid grid-cols-[auto,1fr,auto] items-center gap-3 px-4 py-2 text-sm"
            >
              <span className="font-mono text-[11px] text-slate-400 whitespace-nowrap">
                {row.checkedAt ? format(new Date(row.checkedAt), 'd MMM HH:mm') : '—'}
              </span>
              <span className="flex items-center gap-2 min-w-0">
                <HealthBadge tone={tone} text={text} size="sm" />
                {schemaTag && (
                  <span
                    className="text-[9px] font-mono text-slate-500 border border-slate-700 rounded px-1"
                    title={`kiosk_state schema ${schemaTag}`}
                  >
                    {schemaTag}
                  </span>
                )}
                {launcherVersion && (
                  <span className="truncate text-[11px] text-slate-500">launcher {launcherVersion}</span>
                )}
              </span>
              <span className="text-[11px] text-slate-500 whitespace-nowrap">
                {row.result?.replace(/_/g, ' ') ?? ''}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── v2 sub-components ──────────────────────────────────────────────────

function KioskV2Summary({
  result,
  compareTotal,
}: {
  result: KioskHealthV2Result;
  compareTotal?: number;
}) {
  const { ok, info, warn, fail, incomplete } = result.summary;
  return (
    <div className="rounded-lg border border-slate-700 bg-slate-900/40 px-3 py-2 flex flex-wrap items-center gap-3 text-xs">
      <span className="text-slate-400 font-semibold uppercase tracking-wider">Sections</span>
      <span className="flex items-center gap-1 text-green-400">
        <span className="h-1.5 w-1.5 rounded-full bg-green-400" />
        {ok} OK
      </span>
      {info > 0 && (
        <span className="flex items-center gap-1 text-slate-300">
          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
          {info} info
        </span>
      )}
      {warn > 0 && (
        <span className="flex items-center gap-1 text-amber-400">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
          {warn} warning
        </span>
      )}
      {fail > 0 && (
        <span className="flex items-center gap-1 text-red-400">
          <span className="h-1.5 w-1.5 rounded-full bg-red-400" />
          {fail} critical
        </span>
      )}
      {incomplete > 0 && (
        <span className="flex items-center gap-1 text-slate-400">
          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
          {incomplete} incomplete
        </span>
      )}
      {compareTotal != null && compareTotal > 0 && (
        <span className="ml-auto text-amber-400/80">
          {compareTotal} field{compareTotal === 1 ? '' : 's'} changed since previous snapshot
        </span>
      )}
      {compareTotal === 0 && (
        <span className="ml-auto text-slate-500 italic">no changes vs. previous snapshot</span>
      )}
    </div>
  );
}

function KioskV2SectionList({
  sections,
  compare,
}: {
  sections: KioskV2SectionResult[];
  compare?: KioskCompareResult;
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const primary = sections.filter((s) => s.isPrimary);
  const advanced = sections.filter((s) => !s.isPrimary);

  // Auto-promote advanced sections that aren't OK — surface them next to the
  // primary group so operators never miss a real problem hidden under "advanced".
  const promotedAdvanced = advanced.filter((s) => s.status !== 'ok');
  const collapsedAdvanced = advanced.filter((s) => s.status === 'ok');

  const changedCount = (id: KioskV2SectionResult['id']) =>
    compare?.sectionChangedCounts?.[id] ?? 0;

  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
          Primary sections
        </div>
        <div className="space-y-1.5">
          {primary.map((s) => (
            <KioskV2SectionCard key={s.id} section={s} changedCount={changedCount(s.id)} />
          ))}
        </div>
      </div>

      {promotedAdvanced.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-amber-400/80 mb-1.5">
            Advanced — non-OK ({promotedAdvanced.length}) · auto-promoted
          </div>
          <div className="space-y-1.5">
            {promotedAdvanced.map((s) => (
              <KioskV2SectionCard
                key={s.id}
                section={s}
                changedCount={changedCount(s.id)}
                hint="from advanced"
              />
            ))}
          </div>
        </div>
      )}

      {collapsedAdvanced.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300"
            aria-expanded={showAdvanced}
          >
            {showAdvanced ? (
              <ChevronDownIcon className="h-3.5 w-3.5" />
            ) : (
              <ChevronRightIcon className="h-3.5 w-3.5" />
            )}
            {showAdvanced ? 'Hide' : 'Show'} advanced sections ({collapsedAdvanced.length})
          </button>
          {showAdvanced && (
            <div className="space-y-1.5 mt-1.5">
              {collapsedAdvanced.map((s) => (
                <KioskV2SectionCard key={s.id} section={s} changedCount={changedCount(s.id)} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CompareWithPreviousV2({
  compare,
  previousAt,
  previousSchema,
}: {
  compare: KioskCompareResult;
  previousAt?: string;
  previousSchema: KioskSchema;
}) {
  const [expanded, setExpanded] = useState(false);

  if (!compare.comparable) {
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-800/50 px-4 py-3 text-sm text-slate-400">
        No previous snapshot to compare with for this device yet.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/50">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-center justify-between px-4 py-2.5 text-sm hover:bg-slate-700/30"
        aria-expanded={expanded}
      >
        <span className="flex items-center gap-2">
          {expanded ? (
            <ChevronDownIcon className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronRightIcon className="h-4 w-4 text-slate-400" />
          )}
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            Compare with previous snapshot
          </span>
          <span className="text-[11px] text-slate-500">({formatRelative(previousAt)})</span>
        </span>
        <span className="text-[11px] text-slate-500">
          {compare.totalChanged === 0
            ? 'no fields changed'
            : `${compare.totalChanged} field${compare.totalChanged === 1 ? '' : 's'} changed`}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-slate-700 px-3 py-2 space-y-2">
          {!compare.schemaMatch && (
            <p className="text-[11px] text-slate-400 italic">
              Previous snapshot used kiosk_state {previousSchema}; only shared v1 fields can be compared.
            </p>
          )}
          {compare.totalChanged === 0 ? (
            <div className="px-2 py-1 text-sm text-slate-400">
              No kiosk fields changed since previous snapshot.
            </div>
          ) : (
            <div className="grid grid-cols-[auto,1fr,auto,auto] items-center gap-2 px-1 text-xs">
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Section
              </div>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Field
              </div>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Previous
              </div>
              <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                Now
              </div>
              {compare.changes.map((c, i) => (
                <Fragment key={`${c.section ?? 'v1'}-${c.label}-${i}`}>
                  <div className="px-2 py-1 text-[11px] text-slate-500 font-mono whitespace-nowrap">
                    {c.section ?? 'v1'}
                  </div>
                  <div className="px-2 py-1 text-sm text-slate-300 truncate" title={c.label}>
                    {c.label}
                  </div>
                  <div className="px-2 py-1 font-mono text-xs text-slate-400">
                    {c.previousValue}
                  </div>
                  <div className="px-2 py-1 font-mono text-xs text-amber-400">
                    {c.currentValue}
                  </div>
                </Fragment>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────

export default function DeviceHealthTab({ deviceId, device }: DeviceHealthTabProps) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['device-kiosk-history', deviceId],
    queryFn: () =>
      api.adminGetNeptuneUpdates({ device_id: deviceId, limit: HISTORY_LIMIT }),
    enabled: !!deviceId,
    refetchInterval: 60000,
  });

  const rows: UpdateLogRow[] = useMemo(() => (data?.updates as UpdateLogRow[]) ?? [], [data]);

  // Two distinct concepts:
  //   latestCheckRow    — the most recent update-check row this device sent,
  //                       regardless of whether it included kiosk_state.
  //   latestSnapshotRow — the most recent update-check row that DID include
  //                       kiosk_state. May be older than latestCheckRow if
  //                       the customer's current launcher build pre-dates the
  //                       kiosk telemetry change (vortexnav-client PR #365).
  const latestCheckRow = rows[0];
  const latestCheckHasSnapshot = extractKiosk(latestCheckRow) != null;
  const withKiosk = useMemo(() => rows.filter((r) => extractKiosk(r) != null), [rows]);
  const latestRow = withKiosk[0];
  const previousRow = withKiosk[1];
  const latestState = extractKiosk(latestRow);
  const previousState = extractKiosk(previousRow);

  const latestHealth = computeKioskHealth(latestState);
  const latestSchema = detectKioskSchema(latestState);
  const previousSchema = detectKioskSchema(previousState);
  const v2Result: KioskHealthV2Result | null = useMemo(
    () => (latestSchema === 'v2' && latestState ? computeKioskHealthV2(latestState as KioskStateV2) : null),
    [latestSchema, latestState],
  );
  const compareResult: KioskCompareResult | null = useMemo(
    () => (latestState ? compareKiosk(latestState, previousState ?? null) : null),
    [latestState, previousState],
  );

  // Stale-snapshot condition: a fresh update check came in but didn't include
  // kiosk_state, AND we have an older snapshot to show. Surface this so the
  // operator doesn't read "Last reported N min ago" as freshly current when in
  // fact the running launcher build no longer reports kiosk state.
  const isStaleSnapshot =
    latestCheckRow != null && !latestCheckHasSnapshot && latestRow != null;

  // ─── Copy-report state ────────────────────────────────────────────────
  // IMPORTANT: declare all hooks (useState / useEffect / useCallback) BEFORE
  // any conditional early return. React's Rules of Hooks require a stable
  // call order across renders; bailing out early (loading / error / empty
  // states) above this block would change the hook count between renders
  // and produce a runtime crash (blank page).

  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  useEffect(() => {
    if (copyState === 'idle') return;
    const t = setTimeout(() => setCopyState('idle'), 2000);
    return () => clearTimeout(t);
  }, [copyState]);

  const handleCopyReport = useCallback(async () => {
    try {
      const reportInput: KioskHealthReportInput = {
        generatedAt: new Date(),
        deviceId,
        device: device
          ? {
              name: device.name ?? null,
              serial_number: device.serial_number ?? null,
              la_version: device.la_version ?? null,
              last_seen_at: device.last_seen_at ?? null,
            }
          : null,
        rows: rows as KioskHealthReportRow[],
        latestCheckRow: latestCheckRow as KioskHealthReportRow | undefined,
        latestSnapshotRow: latestRow as KioskHealthReportRow | undefined,
        isStaleSnapshot,
        schema: latestSchema,
        latestState,
        health: latestHealth,
        v2Result,
        compare: compareResult ?? null,
      };
      const text = buildKioskHealthReport(reportInput);
      const ok = await copyToClipboard(text);
      setCopyState(ok ? 'copied' : 'failed');
    } catch {
      setCopyState('failed');
    }
  }, [
    deviceId,
    device,
    rows,
    latestCheckRow,
    latestRow,
    isStaleSnapshot,
    latestSchema,
    latestState,
    latestHealth,
    v2Result,
    compareResult,
  ]);

  // ─── Loading ───────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-24 animate-pulse rounded-lg border border-slate-700 bg-slate-800/40" />
        <div className="h-64 animate-pulse rounded-lg border border-slate-700 bg-slate-800/40" />
        <div className="h-48 animate-pulse rounded-lg border border-slate-700 bg-slate-800/40" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-800 bg-red-900/20 p-4 text-sm text-red-400">
        Failed to load device update history.{' '}
        <button onClick={() => refetch()} className="underline hover:text-red-300">
          Retry
        </button>
      </div>
    );
  }

  // ─── Empty states ──────────────────────────────────────────────────────

  const refreshButton = (
    <button
      type="button"
      onClick={() => refetch()}
      className="flex items-center gap-1.5 rounded-lg bg-slate-700 px-2.5 py-1.5 text-xs text-slate-300 hover:bg-slate-600"
      title="Refetch update history"
    >
      <ArrowPathIcon className="h-3.5 w-3.5" />
      Refresh
    </button>
  );

  const copyButton = (
    <button
      type="button"
      onClick={handleCopyReport}
      className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs ${
        copyState === 'copied'
          ? 'bg-green-900/40 text-green-200 border border-green-700/60'
          : copyState === 'failed'
            ? 'bg-red-900/40 text-red-200 border border-red-700/60'
            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
      }`}
      title="Copy a Markdown health report to the clipboard"
      aria-live="polite"
    >
      {copyState === 'copied' ? (
        <>
          <CheckIcon className="h-3.5 w-3.5" />
          Copied!
        </>
      ) : copyState === 'failed' ? (
        <>
          <XMarkIcon className="h-3.5 w-3.5" />
          Copy failed
        </>
      ) : (
        <>
          <ClipboardDocumentIcon className="h-3.5 w-3.5" />
          Copy report
        </>
      )}
    </button>
  );

  const headerActions = (
    <>
      {copyButton}
      {refreshButton}
    </>
  );

  // (A) No update logs at all for this device.
  if (rows.length === 0) {
    return (
      <div className="space-y-5">
        <HealthModuleCard
          title="Kiosk health"
          status="neutral"
          statusText="No data"
          subtitle="No update checks recorded yet."
          headerActions={headerActions}
          defaultExpanded
        >
          <p className="text-sm text-slate-400">
            Ask the customer to open Launcher → Updates → Check All on the tablet. A snapshot will
            appear here on the next check.
          </p>
        </HealthModuleCard>
      </div>
    );
  }

  // (B) Update logs exist but no kiosk_state has ever been reported.
  if (latestState == null) {
    return (
      <div className="space-y-5">
        <HealthModuleCard
          title="Kiosk health"
          status="neutral"
          statusText="No kiosk snapshot"
          subtitle={
            <>
              {rows.length} update check{rows.length === 1 ? '' : 's'} on record · no kiosk
              telemetry yet
            </>
          }
          headerActions={headerActions}
          defaultExpanded
        >
          <p className="text-sm text-slate-400">
            This device has {rows.length} update check{rows.length === 1 ? '' : 's'} recorded but
            has not reported kiosk state yet. It likely has not received the Launcher 2.42.x+
            telemetry build. After the next OTA wave reaches this device, a snapshot will appear
            here.
          </p>
          <HistoryTimeline rows={rows} />
        </HealthModuleCard>
      </div>
    );
  }

  // (C) Latest snapshot row has kiosk_state — render the full health view.
  const latestCheckLauncher = extractLauncherVersion(latestCheckRow);
  const latestCheckChannel = extractUpdateChannel(latestCheckRow);
  const latestCheckResult = latestCheckRow?.result;

  const snapshotLauncher = extractLauncherVersion(latestRow);
  const snapshotChannel = extractUpdateChannel(latestRow);
  const snapshotResult = latestRow?.result;

  const kioskStatusTone = healthLabelTone(latestHealth.primary);
  const kioskStatusText = healthLabelText(latestHealth.primary);
  const extraBadges: HealthModuleBadge[] = latestHealth.issues
    .filter((l) => l !== latestHealth.primary)
    .map((l) => ({ tone: healthLabelTone(l), text: healthLabelText(l) }));

  // Card-level tone: stale-snapshot also forces an expanded card with warn tone.
  const cardTone: HealthModuleTone = isStaleSnapshot ? 'warn' : kioskStatusTone;
  const cardStatusText = isStaleSnapshot ? 'Snapshot stale' : kioskStatusText;
  const isHealthyAndFresh = latestHealth.isHealthy && !isStaleSnapshot;

  const collapsedSubtitle = (
    <span className="text-xs text-slate-500">
      <span className="text-slate-400">Latest check:</span>{' '}
      {formatRelative(latestCheckRow?.checkedAt)}
      {latestCheckLauncher && (
        <>
          {' '}· launcher{' '}
          <span className="font-mono text-slate-400">{latestCheckLauncher}</span>
        </>
      )}
      {isStaleSnapshot && (
        <span className="text-amber-400/80"> · no kiosk snapshot in this check</span>
      )}
    </span>
  );

  return (
    <div className="space-y-5">
      <HealthModuleCard
        title="Kiosk health"
        status={cardTone}
        statusText={cardStatusText}
        statusBadges={isStaleSnapshot ? [{ tone: kioskStatusTone, text: kioskStatusText }, ...extraBadges] : extraBadges}
        subtitle={collapsedSubtitle}
        headerActions={headerActions}
        defaultExpanded={!isHealthyAndFresh}
      >
        {/* Stale-snapshot banner — only when the latest update check arrived
            from a launcher build that does not report kiosk_state, AND we have
            an older snapshot to show. Calls out the version gap explicitly so
            the operator doesn't misread the snapshot's age as the latest check. */}
        {isStaleSnapshot && (
          <div className="rounded-lg border border-amber-700/50 bg-amber-900/20 p-4">
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 flex-none text-amber-400 mt-0.5" />
              <div className="space-y-1.5">
                <p className="text-sm font-semibold text-amber-200">
                  Latest update check did not include kiosk state.
                </p>
                <p className="text-xs text-amber-100/80">
                  The most recent check ({formatRelative(latestCheckRow?.checkedAt)}
                  {latestCheckLauncher && (
                    <>
                      , launcher{' '}
                      <span className="font-mono">{latestCheckLauncher}</span>
                    </>
                  )}
                  ) did not report kiosk state. The snapshot shown below is from an earlier check
                  ({formatRelative(latestRow?.checkedAt)}
                  {snapshotLauncher && (
                    <>
                      , launcher{' '}
                      <span className="font-mono">{snapshotLauncher}</span>
                    </>
                  )}
                  ). Fresh snapshots will resume once a launcher build containing kiosk telemetry
                  reaches this device.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Detailed check-vs-snapshot timestamps */}
        <div className="space-y-1.5">
          <p className="text-xs text-slate-500">
            <span className="text-slate-400">Latest check:</span>{' '}
            {formatRelative(latestCheckRow?.checkedAt)}
            {latestCheckRow?.checkedAt && (
              <>
                {' '}·{' '}
                <span title={formatTimestamp(latestCheckRow.checkedAt)} className="text-slate-500">
                  {formatTimestamp(latestCheckRow.checkedAt)}
                </span>
              </>
            )}
            {latestCheckLauncher && (
              <>
                {' '}· launcher{' '}
                <span className="font-mono text-slate-400">{latestCheckLauncher}</span>
              </>
            )}
            {latestCheckChannel && (
              <>
                {' '}· channel{' '}
                <span className="font-mono text-slate-400">{latestCheckChannel}</span>
              </>
            )}
            {latestCheckResult && (
              <>
                {' '}·{' '}
                <span className="text-slate-500">{latestCheckResult.replace(/_/g, ' ')}</span>
              </>
            )}
            {isStaleSnapshot && (
              <span className="text-amber-400/80"> · no kiosk snapshot</span>
            )}
          </p>
          {isStaleSnapshot && (
            <p className="text-xs text-slate-500">
              <span className="text-slate-400">Latest kiosk snapshot:</span>{' '}
              {formatRelative(latestRow?.checkedAt)}
              {latestRow?.checkedAt && (
                <>
                  {' '}·{' '}
                  <span title={formatTimestamp(latestRow.checkedAt)} className="text-slate-500">
                    {formatTimestamp(latestRow.checkedAt)}
                  </span>
                </>
              )}
              {snapshotLauncher && (
                <>
                  {' '}· launcher{' '}
                  <span className="font-mono text-slate-400">{snapshotLauncher}</span>
                </>
              )}
              {snapshotChannel && snapshotChannel !== latestCheckChannel && (
                <>
                  {' '}· channel{' '}
                  <span className="font-mono text-slate-400">{snapshotChannel}</span>
                </>
              )}
              {snapshotResult && (
                <>
                  {' '}·{' '}
                  <span className="text-slate-500">{snapshotResult.replace(/_/g, ' ')}</span>
                </>
              )}
            </p>
          )}
          <p className="text-[11px] text-slate-500 italic">
            Shows the last reported kiosk state, not live state. It only updates when the running
            launcher build reports kiosk_state during an update check.
          </p>
        </div>

        {latestHealth.isHealthy && !isStaleSnapshot && (
          <p className="text-xs text-slate-500 italic">
            Kiosk state correct — if the customer's symptom persists, investigate VortexNav /
            WebView / UI path rather than launcher settings.
          </p>
        )}

        {/* Unknown-schema fallback notice — never blocks the rest of the tab. */}
        {latestSchema === 'unknown' && (
          <div className="rounded-lg border border-amber-700/50 bg-amber-900/20 px-3 py-2 text-xs text-amber-200">
            <ExclamationTriangleIcon className="inline h-3.5 w-3.5 mr-1" />
            kiosk_state schema is unknown — the UI is falling back to v1 fields only;
            other sections may be missing until the renderer catches up.
          </div>
        )}

        {/* Schema-aware body */}
        {latestSchema === 'v2' && v2Result ? (
          <>
            <KioskV2Summary
              result={v2Result}
              compareTotal={compareResult?.comparable ? compareResult.totalChanged : undefined}
            />
            <KioskV2SectionList
              sections={v2Result.sections}
              compare={compareResult ?? undefined}
            />
            {compareResult && (
              <CompareWithPreviousV2
                compare={compareResult}
                previousAt={previousRow?.checkedAt}
                previousSchema={previousSchema}
              />
            )}
          </>
        ) : (
          <>
            <CurrentSnapshotCard state={latestState} />
            <CompareWithPrevious
              latest={latestState}
              previous={previousState}
              previousAt={previousRow?.checkedAt}
            />
          </>
        )}

        {/* History */}
        <HistoryTimeline rows={rows} />
      </HealthModuleCard>

      {/* Runtime drift — live SystemUI gesture/geometry vs stored hardening.
          Self-hides when the snapshot predates runtime-drift telemetry. */}
      <RuntimeDriftCard state={latestState} />
    </div>
  );
}
